(ns manage.product-store-form.view
  (:require ["@dnd-kit/core" :as dnd-core]
            ["@dnd-kit/sortable" :as dnd-sortable]
            ["@dnd-kit/utilities" :as dnd-utilities]
            ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [cljs-bean.core :refer [->js]]
            [common.form :as form]
            [common.modal.dialog :refer [deletion-confirmation]]
            [common.modal.view :refer [defmodal]]
            [common.queries :refer [use-mutation]]
            [common.resource.query.product-store :as product-store.q]
            [common.util :refer [spread-props]]
            [common.widgets.input :refer [input]]
            [common.widgets.panel :refer [panel-header]]
            [goog.object :as gobject]
            [manage.product-store-form.hooks :as hooks]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui tile-header [{:keys [handle label]}]
  ($ Box {:sx #js {:display "flex"
                   :gap 1
                   :ml -2
                   :px 2
                   :py 1.5}
          :data-testid :tile-drag-handle}
     handle
     ($ :span {:data-testid :tile-drag-handle-label}
        label)))

(defui tile
  [{:keys [id buttons draggable? drag-label class children]}]
  (let [sortable (dnd-sortable/useSortable #js {:id id})
        setNodeRef (.-setNodeRef sortable)
        attributes (.-attributes sortable)
        listeners (.-listeners sortable)
        transform (.-transform sortable)
        transition (.-transition sortable)
        is-dragging (.-isDragging sortable)]
    ($ Paper
       (spread-props {:class class
                      :data-component :tile}
                     (when draggable?
                       {:ref setNodeRef
                        :sx #js {:transform (dnd-utilities/CSS.Transform.toString transform)
                                 :transition transition
                                 :opacity (if is-dragging 0.5 1)}}))
       ($ Box {:sx (fn [^js theme]
                     #js {:position "sticky"
                          :top 0
                          :display "flex"
                          :alignItems "center"
                          :justifyContent "space-between"
                          :px 2
                          :minHeight "45px"
                          :backgroundColor (-> theme .-palette .-grey (gobject/get "90"))})
               :data-testid :tile-header}
          ($ tile-header
             {:label drag-label
              :handle (when draggable?
                        ($ Icon (spread-props {:sx #js {:cursor "grab"}}
                                              attributes
                                              listeners)
                           "drag_indicator"))})
          (when (some? buttons)
            ($ Box {:sx #js {:display "flex"
                             :gap 1}
                    :data-testid :tile-control-handle}
               buttons)))
       ($ Box {:sx #js {:px 3 :py 2}
               :data-testid :tile-content}
          children))))

(def max-fields-in-overview 3)

(defui edit-field-button
  [{:keys [id]}]
  (let [{:keys [set-edit-field]} (hooks/use-product-store-edit-context)]
    ($ IconButton
       {:on-click #(set-edit-field id)
        :data-testid "ps-field-form-edit-button"}
       ($ Icon "edit"))))

(defui remove-field-button
  [{:keys [field-id field-name]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {delete-field :mutate} (use-mutation (product-store.q/delete-field-mutation {:advertiser-id advertiser-id}))]
    ($ IconButton
       {:on-click #(rf/dispatch [:modal/open {:id :form.product-store/confirm-delete-field
                                              :props {:on-confirm (fn []
                                                                    (delete-field field-id))
                                                      :field-name field-name}}])
        :data-testid "ps-field-form-delete-button"}
       ($ Icon "delete"))))

(defui cancel-edit-button []
  (let [{:keys [clear-edit-field]} (hooks/use-product-store-edit-context)]
    ($ IconButton
       {:on-click #(clear-edit-field)
        :data-testid "ps-new-field-form-cancel-button"}
       ($ Icon "close"))))

(defui confirm-edit-button
  []
  ($ IconButton
     {:type "submit"
      :data-testid "ps-new-field-form-confirm-button"}
     ($ Icon "check")))

(defui view-tile [{:keys [id idx]}]
  (let [field (hooks/use-field id)]
    ($ tile
       {:buttons ($ :<>
                    ($ edit-field-button {:id id})
                    ($ remove-field-button {:field-id id
                                            :field-name (:name field)}))
        :id id
        :draggable? true}
       ($ :div
          ($ Stack {:sx #js {:flex 1
                             :flexDirection "row"
                             :gap 2
                             :alignItems "center"
                             :alignContent "flex-start"
                             :minHeight 40}}
             ($ :div {:data-testid :ps-field-form-type
                      :style {:width 300}}
                (:type field))
             ($ :div {:data-testid :ps-field-form-name
                      :style {:width 300}}
                (:name field))
             ($ :div {:data-testid :ps-field-form-handle-empty-string-is-undefined
                      :style {:width 300}}
                (when (:empty-string-is-undefined field)
                  ($ Chip {:label "Empty values are set as not defined"
                           :sx #js {:alignSelf "flex-start"}}))))
          (when (< idx max-fields-in-overview)
            ($ Chip {:variant "outlined"
                     :data-testid :ps-field-form-hint
                     :label "Included in overview"
                     :color "info"
                     :sx #js {:fontStyle "italic"
                              :alignSelf "flex-start"}}))))))

(defui create-tile [{:keys [idx id]}]
  (let [{:keys [form-control on-submit available-slots]}
        (hooks/use-product-store-field-form id)
        {:keys [control]} form-control]
    ($ :form
       {:on-submit (fn [e]
                     (.preventDefault e)
                     (on-submit))}
       ($ tile
          {:buttons ($ :<>
                       ($ cancel-edit-button)
                       ($ confirm-edit-button))
           :id id
           :draggable? false}
          ($ :div
             ($ Stack {:sx #js {:flex 1
                                :flexDirection "row"
                                :gap 2
                                :alignItems "top"
                                :alignContent "flex-start"
                                :minHeight 60}}
                ($ :div {:data-testid :ps-field-form-type
                         :style {:width 300}}
                   ($ form/select-field
                      {:name "type"
                       :control control
                       :label "Field Type"
                       :form-control-props {:fullWidth true
                                            :size "small"}
                       :field-props {:options available-slots
                                     :fullWidth true
                                     :data-testid "ps-field-form-new-dropdown"}}))
                ($ :div {:data-testid :ps-field-form-name
                         :style {:width 300}}
                   ($ form/form-text-field
                      {:name "name"
                       :control control
                       :label "Name"
                       :field-props {:size "small"
                                     :fullWidth true
                                     :inputProps #js {:maxLength 70}
                                     :placeholder "Name"
                                     :data-testid "ps-field-form-name-input"}}))
                ($ :div {:data-testid :ps-field-form-handle-empty-string-is-undefined
                         :style {:width 300}}
                   ($ form/checkbox
                      {:name "empty-string-is-undefined"
                       :control control
                       :label "Set empty values as not defined?"
                       :field-props {:data-testid :empty-values-not-defined-checkbox}})))
             (when (< idx max-fields-in-overview)
               ($ Chip {:variant "outlined"
                        :data-testid :ps-field-form-hint
                        :label "Included in overview"
                        :color "info"
                        :sx #js {:fontStyle "italic"
                                 :alignSelf "flex-start"}})))))))

(defui edit-tile [{:keys [id idx]}]
  (let [{:keys [form-control on-submit field]}
        (hooks/use-product-store-field-form id)
        {:keys [control]} form-control]
    ($ :form
       {:on-submit (fn [e]
                     (.preventDefault e)
                     (on-submit))}
       ($ tile
          {:buttons ($ :<>
                       ($ cancel-edit-button)
                       ($ confirm-edit-button))
           :id id
           :draggable? false}
          ($ :div
             ($ Stack {:sx #js {:flex 1
                                :flexDirection "row"
                                :gap 2
                                :alignItems "center"
                                :alignContent "flex-start"
                                :minHeight 40}}
                ($ :div {:data-testid :ps-field-form-type
                         :style {:width 300}}
                   (:type field))
                ($ :div {:data-testid :ps-field-form-name
                         :style {:width 300}}
                   (:name field))
                ($ :div {:data-testid :ps-field-form-handle-empty-string-is-undefined
                         :style {:width 300}}
                   ($ form/checkbox
                      {:name "empty-string-is-undefined"
                       :control control
                       :label "Set empty values as not defined?"
                       :field-props {:data-testid :empty-values-not-defined-checkbox}})))
             (when (< idx max-fields-in-overview)
               ($ Chip {:variant "outlined"
                        :data-testid :ps-field-form-hint
                        :label "Included in overview"
                        :color "info"
                        :sx #js {:fontStyle "italic"
                                 :alignSelf "flex-start"}})))))))

(defui field-block [{:keys [idx id]}]
  (let [in-edit? (hooks/use-in-edit? id)]
    (if in-edit?
      ($ edit-tile {:idx idx :id id})
      ($ view-tile {:idx idx :id id}))))

(defui field-blocks [{:keys [itemIds move-field]}]
  (let [sensors (dnd-core/useSensors
                 (dnd-core/useSensor dnd-core/PointerSensor)
                 (dnd-core/useSensor dnd-core/KeyboardSensor
                                     #js {:coordinateGetter dnd-sortable/sortableKeyboardCoordinates}))
        handleDragEnd (uix/use-callback
                       (fn [^js event]
                         (let [active-id (-> event .-active .-id)
                               over-id (-> event .-over .-id)]
                           (when (not= active-id over-id)
                             (let [old-index (.indexOf itemIds active-id)
                                   new-index (.indexOf itemIds over-id)]
                               (move-field {:oldIndex old-index :newIndex new-index})))))
                       [itemIds move-field])]
    ($ dnd-core/DndContext
       {:sensors sensors
        :collisionDetection dnd-core/closestCenter
        :onDragEnd handleDragEnd}
       ($ dnd-sortable/SortableContext
          {:items (->js itemIds)
           :strategy dnd-sortable/verticalListSortingStrategy}
          ($ Stack
             {:gap 3
              :sx #js {:mb 3}}
             (for [[idx id] (mc/seq-indexed itemIds)]
               ($ field-block
                  {:key id
                   :id id
                   :idx idx})))))))

(defui action-block []
  (let [{:keys [edit-field-id set-edit-field]} (hooks/use-product-store-edit-context)
        idx (inc (hooks/use-product-fields-count))]
    (if (= :new-field edit-field-id)
      ($ create-tile {:idx idx
                      :id :new-field})
      ($ tile {}
         ($ Button
            {:on-click #(set-edit-field :new-field)
             :variant "text"
             :start-icon ($ Icon "add")
             :data-testid "ps-add-field-block-button"}
            "Add field block")))))

(defui blocks-content
  []
  (let [{:keys [field-ids is-error]} (hooks/use-product-field-ids)
        {:keys [move-field]} (hooks/use-product-store-move-field)]
    (if is-error
      ($ Typography {:font-style "italic"
                     :font-weight "600"
                     :color "error"
                     :sx #js {:mt 1}}
         "No product store was created for this advertiser. Please contact support to help you with this.")
      ($ :<>
         ($ panel-header
            {:title "Configure the fields to store your product data"
             :data-testid "panel-header-title"})
         (when (seq field-ids)
           ($ field-blocks
              {:itemIds field-ids
               :move-field move-field}))
         ($ action-block)))))

(defui blocks
  []
  ($ hooks/product-store-edit-provider
     ($ uix/suspense {:fallback ($ Stack
                                   {:height "100%"
                                    :alignItems "center"
                                    :justifyContent "center"}
                                   ($ CircularProgress))}
        ($ blocks-content))))

(defmodal confirm-delete :form.product-store/confirm-delete-field
  [{:keys [on-confirm field-name]}]
  (r/with-let [filled-name (r/atom "")]
    [deletion-confirmation
     {:on-submit #(on-confirm)
      :submit-button-disabled? (not= field-name @filled-name)
      :entity-name "product store field"}
     [:p "Deleting this field can result in invalid product selectors, which can cause unexpected behaviour in live adsets. Please type in the name of the field to confirm."]
     ($ input
        {:inputProps #js {:maxLength 70}
         :onChange (fn [^js/Event event]
                     (if (and event (instance? js/Event (.-nativeEvent event)))
                       (reset! filled-name (-> event .-target .-value))
                       (reset! filled-name event)))
         :data-testid "ps-delete-modal-input"
         :sx #js {:mt 1}})]))
